import { Loader2, ChevronLeft, ChevronRight, Search, type LucideIcon } from 'lucide-react'
import { useMemo, useState } from 'react'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import type { CampoExterno, MetadataResponse, PaginatedListado, RecursoListado } from '../types'
import { formatMoneda, formatNumero, regionTerminoCap, terminoComprobante } from '../lib/paisMapa'

interface ColumnDef {
  key: string
  label: string
  /** Forma de derivar el valor desde una fila (default = row[key]). */
  accessor?: (row: Record<string, unknown>) => unknown
  /** Formatter visual (default = formato genérico). */
  format?: (value: unknown, row: Record<string, unknown>) => React.ReactNode
}

export interface RowAction {
  label: string
  icon?: LucideIcon
  onClick: (row: Record<string, unknown>) => void
}

interface Props {
  configId: number
  recurso: RecursoListado
  metadata?: MetadataResponse | null
  data?: PaginatedListado
  isLoading: boolean
  isFetching?: boolean
  page: number
  pageSize: number
  onPageChange: (page: number) => void
  onPageSizeChange?: (size: number) => void
  /** Si está presente, agrega una columna de acciones al final con un botón por fila. */
  rowAction?: RowAction
  /** Keys de `campos_externos` a NO renderizar como columna (ej. redundantes con columnas fijas). */
  omitCampos?: string[]
}

const TZ = 'America/Guayaquil'

function formatDate(v: unknown): string {
  if (!v) return '—'
  const s = String(v)
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s // YYYY-MM-DD plano
  try {
    return new Intl.DateTimeFormat('es-EC', {
      timeZone: TZ,
      dateStyle: 'short',
      timeStyle: 'short',
    }).format(new Date(s))
  } catch {
    return s
  }
}

function formatDecimal(v: unknown, configId: number, decimals = 2): string {
  if (v === null || v === undefined || v === '') return '—'
  const n = typeof v === 'string' ? parseFloat(v) : (v as number)
  if (Number.isNaN(n)) return String(v)
  return formatNumero(n, configId, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })
}

function formatBool(v: unknown): React.ReactNode {
  if (v === true || v === 'true') return <Badge variant="outline" className="border-green-500 text-green-600 dark:border-green-700 dark:text-green-400">Sí</Badge>
  if (v === false || v === 'false') return <Badge variant="outline" className="border-gray-400 text-gray-500">No</Badge>
  return '—'
}

// ---- Columnas fijas por recurso ----

function columnsByRecurso(configId: number): Record<RecursoListado, ColumnDef[]> {
  const money = (v: unknown) => formatMoneda(v as number | string | null | undefined, configId)
  return {
  participantes: [
    { key: 'id', label: 'ID' },
    {
      key: 'nombre_completo',
      label: 'Nombre',
      accessor: (r) => [r.nombre, r.apellido].filter(Boolean).join(' '),
    },
    { key: 'email', label: 'Email' },
    { key: 'telefono', label: 'Teléfono' },
    { key: 'provincia_nombre', label: regionTerminoCap(configId) },
    { key: 'nivel', label: 'Nivel', format: (v) => v ? <Badge variant="outline" className="capitalize">{String(v)}</Badge> : '—' },
    { key: 'puntos_disponibles', label: 'Puntos disponibles', format: (v) => formatDecimal(v, configId) },
    { key: 'activo', label: 'Activo', format: (v) => formatBool(v) },
    { key: 'created_at', label: 'Registro', format: (v) => formatDate(v) },
  ],
  facturas: [
    { key: 'id', label: 'ID' },
    { key: 'fecha_carga', label: 'Carga', format: (v) => formatDate(v) },
    { key: 'establecimiento', label: 'Establecimiento' },
    { key: 'numero_factura', label: `Núm. ${terminoComprobante(configId)}` },
    { key: 'monto_participantes', label: 'Monto programa', format: money },
    { key: 'monto', label: 'Monto bruto', format: money },
    { key: 'puntos_otorgados', label: 'Puntos', format: (v) => formatDecimal(v, configId) },
    {
      key: 'estado',
      label: 'Estado',
      format: (v) => {
        if (!v) return '—'
        const raw = String(v)
        const norm = raw.toLowerCase().replace(/[_\s]+/g, ' ').trim()
        const green = ['aprobada', 'confirmada', 'validada', 'corregida']
        const red = ['fecha invalida', 'fecha inválida', 'factura incompleta', 'rechazada']
        const yellow = ['sin productos', 'duplicada', 'pendiente']
        const cls = green.includes(norm)
          ? 'border-green-500 text-green-600 dark:border-green-700 dark:text-green-400'
          : red.includes(norm)
            ? 'border-red-500 text-red-600 dark:border-red-700 dark:text-red-400'
            : yellow.includes(norm)
              ? 'border-yellow-500 text-yellow-600 dark:border-yellow-700 dark:text-yellow-400'
              : 'border-gray-400 text-gray-600 dark:border-gray-600 dark:text-gray-300'
        return <Badge variant="outline" className={`capitalize ${cls}`}>{raw}</Badge>
      },
    },
  ],
  canjes: [
    { key: 'id', label: 'ID' },
    {
      key: 'fecha',
      label: 'Fecha',
      accessor: (r) => r.fecha ?? r.created_at,
      format: (v) => formatDate(v),
    },
    {
      key: 'participante',
      label: 'Participante',
      accessor: (r) => r.participante_nombre ?? r.participante_id,
    },
    {
      key: 'operacion',
      label: 'Operación',
      accessor: (r) => r.giftpoint_operacion ?? r.producto_nombre,
      format: (v) => (v ? <Badge variant="outline" className="capitalize">{String(v)}</Badge> : '—'),
    },
    {
      key: 'puntos',
      label: 'Puntos',
      accessor: (r) => r.puntos_usados ?? r.puntos,
      format: (v) => formatDecimal(v, configId),
    },
    { key: 'monto', label: 'Monto', format: money },
    { key: 'estado', label: 'Estado', format: (v) => v ? <Badge variant="outline" className="capitalize">{String(v)}</Badge> : '—' },
  ],
  productos: [
    { key: 'id', label: 'ID' },
    { key: 'codigo', label: 'Código' },
    { key: 'nombre', label: 'Nombre' },
    { key: 'puntos_por_unidad', label: 'Puntos/unidad', format: (v) => formatDecimal(v, configId) },
    { key: 'cantidad_por_paquete', label: 'Unidades/caja' },
    { key: 'precio_referencia', label: 'Precio ref.', format: money },
    { key: 'activo', label: 'Activo', format: (v) => formatBool(v) },
  ],
  'movimientos-puntos': [
    { key: 'id', label: 'ID' },
    { key: 'fecha', label: 'Fecha', format: (v) => formatDate(v) },
    { key: 'participante_id', label: 'Participante' },
    { key: 'tipo', label: 'Tipo', format: (v) => v ? <Badge variant="outline" className="capitalize">{String(v)}</Badge> : '—' },
    { key: 'puntos', label: 'Puntos', format: (v) => formatDecimal(v, configId) },
    { key: 'saldo', label: 'Saldo', format: (v) => formatDecimal(v, configId) },
    { key: 'concepto', label: 'Concepto' },
  ],
  }
}

function dynamicColumnDef(c: CampoExterno, configId: number): ColumnDef {
  return {
    key: c.key,
    label: c.label,
    // datos_extra puede traer {key}_nombre resuelto desde el catálogo.
    accessor: (row) => {
      const extra = (row.datos_extra as Record<string, unknown> | undefined) ?? {}
      const nombre = extra[`${c.key}_nombre`]
      if (nombre !== undefined && nombre !== null && nombre !== '') return nombre
      return extra[c.key] ?? row[c.key]
    },
    format: c.tipo === 'bool' ? (v) => formatBool(v) : c.tipo === 'number' ? (v) => formatDecimal(v, configId) : undefined,
  }
}

function defaultCell(value: unknown): React.ReactNode {
  if (value === null || value === undefined || value === '') return '—'
  if (typeof value === 'object') return <code className="text-xs">{JSON.stringify(value)}</code>
  return String(value)
}

export function DynamicDataTable({
  configId,
  recurso,
  metadata,
  data,
  isLoading,
  isFetching,
  page,
  pageSize,
  onPageChange,
  rowAction,
  omitCampos,
}: Props) {
  const columns = useMemo<ColumnDef[]>(() => {
    const base = columnsByRecurso(configId)[recurso] ?? []
    // Solo agregamos columnas dinámicas a recursos donde aplica (Participantes principalmente).
    if (recurso === 'participantes' && metadata?.campos_externos) {
      const omit = new Set(omitCampos ?? [])
      const dinamicas = metadata.campos_externos
        .filter((c) => !omit.has(c.key))
        .map((c) => dynamicColumnDef(c, configId))
      return [...base, ...dinamicas]
    }
    return base
  }, [configId, recurso, metadata, omitCampos])

  const totalPages = data?.total_pages ?? 1
  const total = data?.total ?? 0
  const start = total > 0 ? (page - 1) * pageSize + 1 : 0
  const end = Math.min(page * pageSize, total)

  // Búsqueda libre client-side sobre las filas de la página actual (no consulta
  // el servidor). Filtra por el valor mostrado de cada columna, por lo que cubre
  // también columnas derivadas (nombre_completo) y dinámicas (datos_extra).
  const [busqueda, setBusqueda] = useState('')
  const visibleItems = useMemo(() => {
    const items = data?.items ?? []
    const term = busqueda.trim().toLowerCase()
    if (!term) return items
    return items.filter((row) =>
      columns.some((c) => {
        const value = c.accessor ? c.accessor(row) : row[c.key]
        return value != null && String(value).toLowerCase().includes(term)
      }),
    )
  }, [data, columns, busqueda])

  return (
    <Card className="border border-gray-300 dark:border-gray-700">
      <CardContent className="p-0">
        <div className="flex items-center justify-end border-b border-gray-300 dark:border-gray-700 p-3">
          <div className="relative w-full sm:w-64">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Buscar en la página..."
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              className="pl-8 h-8 text-sm"
            />
          </div>
        </div>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                {columns.map((c) => (
                  <TableHead key={c.key} className="whitespace-nowrap">{c.label}</TableHead>
                ))}
                {rowAction && (
                  <TableHead className="whitespace-nowrap text-right">Acciones</TableHead>
                )}
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <TableRow key={i}>
                    {columns.map((c) => (
                      <TableCell key={c.key}>
                        <Skeleton className="h-5 w-24" />
                      </TableCell>
                    ))}
                    {rowAction && (
                      <TableCell>
                        <Skeleton className="h-7 w-20 ml-auto" />
                      </TableCell>
                    )}
                  </TableRow>
                ))
              ) : visibleItems.length > 0 ? (
                visibleItems.map((row, idx) => (
                  <TableRow key={String(row.id ?? idx)}>
                    {columns.map((c) => {
                      const value = c.accessor ? c.accessor(row) : row[c.key]
                      return (
                        <TableCell key={c.key} className="whitespace-nowrap">
                          {c.format ? c.format(value, row) : defaultCell(value)}
                        </TableCell>
                      )
                    })}
                    {rowAction && (
                      <TableCell className="whitespace-nowrap text-right">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => rowAction.onClick(row)}
                          className="h-7"
                        >
                          {rowAction.icon ? <rowAction.icon className="h-3.5 w-3.5 mr-1.5" /> : null}
                          {rowAction.label}
                        </Button>
                      </TableCell>
                    )}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={columns.length + (rowAction ? 1 : 0)}
                    className="text-center py-12 text-sm text-muted-foreground"
                  >
                    {busqueda.trim()
                      ? 'Sin coincidencias para la búsqueda en esta página.'
                      : 'Sin resultados para los filtros aplicados.'}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>

        <div className="flex items-center justify-between border-t border-gray-300 dark:border-gray-700 p-3 text-sm">
          <div className="text-muted-foreground inline-flex items-center gap-2">
            {isFetching && <Loader2 className="h-3 w-3 animate-spin" />}
            {total > 0
              ? <>Mostrando <strong>{start}</strong>–<strong>{end}</strong> de <strong>{total}</strong></>
              : 'Sin datos'}
          </div>
          <div className="inline-flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onPageChange(page - 1)}
              disabled={!data?.has_prev || isFetching}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-xs text-muted-foreground tabular-nums">
              Página {page} / {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onPageChange(page + 1)}
              disabled={!data?.has_next || isFetching}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
